import './FailReportItems.scss';

import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useResourceParams } from '@refinedev/core';
import { Button, Empty, Flex, Modal, Skeleton, Table } from 'antd';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUiContext } from '@/contexts/UiContext';
import { FailReportItem } from '@/model/FailReport/FailReportItem';

import FailReportItemForm from './FailReportItemForm/FailReportItemForm';
import useFailReportItems from './useFailReportItems';

interface FailReportItemsProps {
  failReport?: number;
  failReportItems: FailReportItem[];
  setFailReportItems: (items: FailReportItem[]) => void;
  canEditItems?: true;
  refetchResource?: () => void;
}

/**
 * Metadata: construye una key estable para items locales sin id y evita remounts provocados por Math.random().
 */
const getFailReportItemRowKey = (record: FailReportItem) => {
  if (record.id) {
    return record.id.toString();
  }

  return [
    record.product_type ?? 'type',
    record.product ?? record.device ?? record.unidad ?? 'item',
    record.name ?? 'name',
    record.serial_number ?? 'serial',
  ].join('-');
};

const FailReportItems = memo((props: FailReportItemsProps) => {
  const { t } = useTranslation();
  const { isMediumScreen } = useUiContext();
  const { failReport, failReportItems, setFailReportItems, refetchResource, canEditItems } = props;
  const [showEditFooter, setShowEditFooter] = useState(false);
  const [editingItem, setEditingItem] = useState<FailReportItem | null>(null);
  const [openModal, setOpenModal] = useState(false);
  // Action de la ruta
  const { action } = useResourceParams();
  const showResource = action === 'show';
  // Items
  const { isLoading, refetch, handleDelete, saveItems } = useFailReportItems(
    failReport,
    failReportItems,
    setFailReportItems,
    showEditFooter,
  );
  const itemsToShow = useMemo(
    () => failReportItems?.filter(item => !item.toDelete) ?? [],
    [failReportItems],
  );
  // Determina si el modal se abre en modo solo lectura.
  const isReadOnly = useMemo(
    () => !canEditItems && !showEditFooter && !!editingItem?.id,
    [canEditItems, showEditFooter, editingItem],
  );
  const closeModal = useCallback(() => {
    setOpenModal(false);
    setEditingItem(null);
  }, []);

  return (
    <div className="FailReportItems">
      <div className="FailReportItemsTitle">{t('Elementos relacionados al reporte')}</div>
      <div className="FailReportItemsList">
        {!!failReport && isLoading ? (
          <Skeleton />
        ) : (
          <Table<FailReportItem>
            tableLayout="fixed"
            rowKey={getFailReportItemRowKey}
            dataSource={itemsToShow}
            locale={{ emptyText: <Empty description={t('No hay elementos cargados')} /> }}
            footer={() => (
              <div className="FailReportItemsListButtons">
                {showEditFooter && (
                  <>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setShowEditFooter(false);

                        void refetch().then(({ data }) => {
                          if (data) {
                            setFailReportItems(data.data);
                          }
                        });
                      }}
                      size={isMediumScreen ? 'small' : 'middle'}>
                      {t('Cancelar')}
                    </Button>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setOpenModal(true)}
                      size={isMediumScreen ? 'small' : 'middle'}>
                      {t('Cargar elemento')}
                    </Button>
                    <Button
                      icon={<SaveOutlined />}
                      type="primary"
                      onClick={() =>
                        Modal.confirm({
                          title: t('¿Estás seguro de que deseas guardar los cambios?'),
                          content: t('Esta acción no se puede deshacer'),
                          onOk: () =>
                            void saveItems().then(() => {
                              if (refetchResource) {
                                void refetchResource();
                              }

                              setShowEditFooter(false);
                            }),
                        })
                      }
                      size={isMediumScreen ? 'small' : 'middle'}>
                      {t('Guardar cambios')}
                    </Button>
                  </>
                )}
                {canEditItems && !showEditFooter && (
                  <Button
                    icon={showResource ? <EditOutlined /> : <PlusOutlined />}
                    onClick={() => {
                      if (showResource) {
                        setShowEditFooter(true);
                      } else {
                        setOpenModal(true);
                      }
                    }}
                    size={isMediumScreen ? 'small' : 'middle'}>
                    {showResource ? t('Editar elementos') : t('Cargar elemento')}
                  </Button>
                )}
              </div>
            )}
            pagination={false}
            scroll={{ x: 600 }}>
            <Table.Column title={t('Elemento')} render={(item: FailReportItem) => item.name} />
            <Table.Column
              title={t('Número de serie')}
              render={(item: FailReportItem) => item.serial_number}
            />
            <Table.Column title={t('Comentario')} render={(item: FailReportItem) => item.comment} />

            <Table.Column
              fixed="right"
              width={100}
              render={(_, record) => (
                <Flex gap={8}>
                  <Button
                    size="small"
                    icon={canEditItems || !record?.id ? <EditOutlined /> : <EyeOutlined />}
                    onClick={() => {
                      setEditingItem(record as FailReportItem);
                      setOpenModal(true);
                      if (showResource && canEditItems && !showEditFooter) {
                        setShowEditFooter(true);
                      }
                    }}
                  />

                  {(canEditItems || !record?.id) && (
                    <Button
                      className="FailReportItemActionsDeleteButton"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: t('Confirmar eliminación'),
                          content: t('¿Está seguro de que desea eliminar este elemento?'),
                          onOk: () => {
                            handleDelete(record);
                            if (showResource && !showEditFooter) {
                              setShowEditFooter(true);
                            }
                          },
                        });
                      }}
                    />
                  )}
                </Flex>
              )}
            />
          </Table>
        )}
        <Modal
          open={openModal}
          onCancel={closeModal}
          width={600}
          footer={null}
          maskClosable={false}
          destroyOnHidden={true}
          centered
          title={
            isReadOnly
              ? t('Detalle del elemento')
              : editingItem
                ? t('Editar elemento')
                : t('Cargar elemento')
          }>
          <FailReportItemForm
            failReportItems={failReportItems}
            setFailReportItems={setFailReportItems}
            closeModal={closeModal}
            editingItem={editingItem}
            isReadOnly={isReadOnly}
          />
        </Modal>
      </div>
    </div>
  );
});

FailReportItems.displayName = 'FailReportItems';

export default FailReportItems;
